import Dialog from '@app/src/components/Dialog'
import { useAccount } from '@app/src/context/AccountContext'
import useProviderType from '@app/src/hooks/useProviderType'
import { getDateWithoutTimeFromString } from '@app/src/utils/helpersTs'
import type { AssessmentTemplate } from '@app/src/types/resourceExplorer'
import { formatLevelLabel } from '@app/src/utils/assessmentLevelTranslations'
import {
  AccessTimeOutlined,
  DomainOutlined,
  PublishedWithChangesOutlined,
  RadarOutlined,
  StarOutlined,
  SubjectOutlined,
} from '@mui/icons-material'
import { Box, Button, List, ListItem, ListItemIcon, ListItemText, Stack, Typography } from '@mui/material'
import React, { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import type { BulkCreateExpectationFormData } from '../types'
import { BulkCreateExpectationFormField } from '../types'

const SummaryRow: React.FC<{ icon: React.ReactNode; label: string; value: React.ReactNode }> = ({
  icon,
  label,
  value,
}) => (
  <ListItem
    sx={{
      bgcolor: 'grey.50',
      borderRadius: 2,
      border: '1px solid rgba(0, 0, 0, 0.12)',
      mb: 1,
      px: 2,
      py: 1.5,
    }}
  >
    <ListItemIcon sx={{ minWidth: 56 }}>{icon}</ListItemIcon>
    <ListItemText primary={label} />
    {typeof value === 'string' ? <Typography variant="subtitle1">{value}</Typography> : value}
  </ListItem>
)

const SummaryStep: React.FC<{
  eligibleCount: number
  importantKpisCount: number
  assessmentTemplate: AssessmentTemplate | undefined
}> = ({ eligibleCount, importantKpisCount, assessmentTemplate }) => {
  const { formatMessage, formatDate } = useIntl()
  const { account } = useAccount()
  const providerType = useProviderType()
  const { watch } = useFormContext<BulkCreateExpectationFormData>()
  const level = watch(BulkCreateExpectationFormField.TargetAssessmentLevel)
  const deadline = watch(BulkCreateExpectationFormField.Deadline)
  const [isDescriptionDialogOpen, setIsDescriptionDialogOpen] = useState(false)
  const assessmentTemplateName = assessmentTemplate?.name ?? ''
  const levelLabel = formatLevelLabel(assessmentTemplate, level, formatMessage)

  const derivedTitle = formatMessage(
    { id: 'bulkCreateExpectation.steps.summary.expectationTitle' },
    { level: levelLabel, assessmentName: assessmentTemplateName },
  )

  const formattedDeadline = deadline
    ? formatDate(getDateWithoutTimeFromString(deadline), { year: 'numeric', month: 'long', day: 'numeric' })
    : ''

  return (
    <Stack>
      <List>
        <SummaryRow
          icon={<PublishedWithChangesOutlined />}
          label={formatMessage({ id: 'bulkCreateExpectation.steps.summary.titleRow' })}
          value={derivedTitle}
        />
        <SummaryRow
          icon={<RadarOutlined />}
          label={formatMessage({ id: 'bulkCreateExpectation.steps.summary.targetRow' })}
          value={levelLabel}
        />
        <SummaryRow
          icon={<SubjectOutlined />}
          label={formatMessage({ id: 'expectation.summary.introductionSectionTitle' }, { providerType })}
          value={
            <Button variant="outlined" size="small" onClick={() => setIsDescriptionDialogOpen(true)}>
              {formatMessage({ id: 'form.createAutomation.summary.expectation.descriptionButtonLabel' })}
            </Button>
          }
        />
        <SummaryRow
          icon={<AccessTimeOutlined />}
          label={formatMessage({ id: 'bulkCreateExpectation.steps.summary.deadlineRow' })}
          value={formattedDeadline}
        />
        <SummaryRow
          icon={<DomainOutlined />}
          label={formatMessage({ id: 'bulkCreateExpectation.steps.summary.companiesRow' }, { providerType })}
          value={formatMessage(
            { id: 'bulkCreateExpectation.steps.summary.companiesValue' },
            { count: eligibleCount, providerType },
          )}
        />
        <SummaryRow
          icon={<StarOutlined />}
          label={formatMessage({ id: 'bulkCreateExpectation.steps.summary.importantKpis' })}
          value={formatMessage(
            { id: 'bulkCreateExpectation.steps.summary.importantKpisSelected' },
            { count: importantKpisCount },
          )}
        />
      </List>

      <Dialog
        open={isDescriptionDialogOpen}
        onClose={() => setIsDescriptionDialogOpen(false)}
        title={formatMessage(
          { id: 'bulkCreateExpectation.steps.summary.descriptionDialogTitle' },
          { providerType, count: eligibleCount },
        )}
        content={
          <Stack spacing={2}>
            <Box borderRadius={2} p={2} bgcolor="grey.100">
              <Typography variant="body1" color="text.secondary" whiteSpace="pre-wrap">
                {formatMessage(
                  { id: 'expectation.introductionDescription' },
                  {
                    accessor: account?.organization?.name ?? '',
                    providerType,
                    level: levelLabel,
                    assessmentName: assessmentTemplateName,
                  },
                )}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {formatMessage({ id: 'expectation.summary.introductionHelperText' }, { providerType })}
            </Typography>
          </Stack>
        }
        buttons={[
          {
            onClick: () => setIsDescriptionDialogOpen(false),
            label: formatMessage({ id: 'general.close' }),
            variant: 'contained',
          },
        ]}
      />
    </Stack>
  )
}

export default SummaryStep
